import { CurrencyEnum, currencyEnumArray, Employee, WorkflowRule, WorkflowScopeEnum, WorkflowTriggerEnum } from "@finway-group/shared/lib/models"
import { Col, Form, Input, Radio, Row, Select, Image, Tooltip } from "antd"
import { FormInstance } from "antd/lib/form"
import { Store } from "antd/lib/form/interface"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import UserImage from "Components/UserImage"
import { adjustWorkflowInitData, convertApproverFormData, inititalWorkflowData } from "Features/pages/workflows/workflow.utils"
import { useSubCostCenters } from "Shared/hooks/costCenter.hooks"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { useVendors } from "Shared/hooks/vendor.hooks"
import { useWorkflow } from "Shared/hooks/workflow.hooks"
import { getPopupAnchor } from "Shared/utils"
import { getResourcesForTrigger } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import useUpdateEffect from "Shared/utils/hooks/useUpdateEffect"
import { generateRules } from "../rules/workflow.rules"
import { useCostCenters2 } from "Shared/hooks/costCenter2.hooks"
import CurrencyInput from "Components/currencyInput"
import { Currencies, ORANGE } from "@finway-group/shared/lib/consts"
import { parseCurrencyInput } from "Components/currencyInput/config"
import WorkflowFormRules from "./workflowRule.form"
import { ExclamationCircleTwoTone } from "@ant-design/icons"
import { useCompany } from "Shared/hooks/company.hooks"
import { getTooltipPopupContainer } from "Shared/utils/helpers"

const renderTriggerObjectOption = (triggerObject: any, triggerType: WorkflowTriggerEnum, index: number, showCostCentersIds: boolean = true) => {
    switch (triggerType) {
        case WorkflowTriggerEnum.USER:
            return (
                <Select.Option key={index} label={`${triggerObject.firstName} ${triggerObject.lastName}`} value={triggerObject.id}>
                    <div className="flex items-center">
                        <UserImage user={triggerObject} />
                        <span>{`${triggerObject.firstName} ${triggerObject.lastName}`}</span>
                    </div>
                </Select.Option>
            )
        case WorkflowTriggerEnum.COST_CENTER:
            return (
                <Select.Option key={index} value={triggerObject._id}>
                    {showCostCentersIds ? `ID ${triggerObject.id} - ${triggerObject.name}` : triggerObject.name}
                </Select.Option>
            )
        case WorkflowTriggerEnum.VENDOR:
        case WorkflowTriggerEnum.COST_CENTER_2:
            return (
                <Select.Option key={index} value={triggerObject._id}>
                    {triggerObject.name}
                </Select.Option>
            )
        default:
            return <></>
    }
}

export const ALL_TRIGGER_OBJECTS = "ALL_TRIGGER_OBJECTS"
export const RESPONSIBLE_USER_APPROVER = "RESPONSIBLE_USER_APPROVER"
export const SUPERIOR_APPROVER = "SUPERIOR_APPROVER"

interface WorkflowFormInterface {
    isNew: boolean
    formInstance: FormInstance
    onSubmit: (values: Store) => void
}

export const WorkflowForm: React.FC<WorkflowFormInterface> = ({ formInstance, isNew, onSubmit }) => {
    const { t } = useTranslation()
    const costCenters = useSubCostCenters(true)
    const vendors = useVendors(true)
    const employees = useEmployees(false, false, true)
    const costCenter2s = useCostCenters2()
    const workflowDetails = useWorkflow()
    const getInitialData = () => (!isNew && workflowDetails ? adjustWorkflowInitData(workflowDetails) : inititalWorkflowData(costCenters, vendors, employees))
    const [workflow, setWorkflow] = useStateIfMounted<any>(getInitialData())
    const { showCostCentersIds } = useCompany()

    const [resources, setResources] = useStateIfMounted<any[]>(
        getResourcesForTrigger(workflow.triggerType, costCenters, vendors, employees, costCenter2s, workflow.triggerReferenceIds)
    )
    const filterApprovers = (approvers: Employee[]) =>
        approvers.filter(
            (e) => !e.activeCompanyProfile?.deleted && (e.activeCompanyProfile?.isAdmin || e.activeCompanyProfile?.isApprover) && !workflow.triggerReferenceIds.includes(e.id)
        )
    const [possibleApprovers, setPossibleApprovers] = useStateIfMounted(filterApprovers(employees))
    const rules = generateRules(formInstance)

    // initialize form
    useEffect(() => updateWorkflow(getInitialData()), [])

    useUpdateEffect(() => {
        const newResources = getResourcesForTrigger(workflow.triggerType, costCenters, vendors, employees, costCenter2s, workflow.triggerReferenceIds)
        setResources(newResources)
        updateWorkflow({ triggerReferenceIds: [] })
    }, [workflow.triggerType])

    useUpdateEffect(() => {
        setPossibleApprovers(filterApprovers(employees))
    }, [workflow.triggerReferenceIds])

    const updateWorkflow = (data: any, shouldValidate: boolean = false, nameList: string[] = []) => {
        setWorkflow((old: any) => ({ ...old, ...data }))
        formInstance.setFieldsValue({
            ...formInstance.getFieldsValue(),
            ...data,
        })
        if (shouldValidate) formInstance.validateFields(nameList)
    }

    const getTriggerObjectContext = (triggerObjectId: any) => {
        let label, totalNumForAll

        switch (workflow.triggerType) {
            case WorkflowTriggerEnum.USER:
                const employee = employees.find(({ id }: any) => id === triggerObjectId)
                label = `${employee?.firstName} ${employee?.lastName}`
                totalNumForAll = employees.length
                break
            case WorkflowTriggerEnum.VENDOR:
                label = vendors.find(({ id }: any) => id === triggerObjectId)?.name
                totalNumForAll = vendors.length
                break
            case WorkflowTriggerEnum.COST_CENTER:
                const costCenter = costCenters.find(({ _id }: any) => _id === triggerObjectId)
                label = showCostCentersIds ? `ID ${costCenter?.id} - ${costCenter?.name}` : costCenter?.name
                totalNumForAll = costCenters.length
                break
            case WorkflowTriggerEnum.COST_CENTER_2:
                label = costCenter2s.find(({ _id }: any) => _id === triggerObjectId)?.name
                totalNumForAll = costCenter2s.length
                break
            default:
                break
        }

        return { totalNumForAll, label }
    }

    const renderTriggerObjectLabel = (triggerObjectId: any) => {
        const { totalNumForAll, label } = getTriggerObjectContext(triggerObjectId)
        const isLastElement = workflow.triggerReferenceIds.slice(-1)[0] === triggerObjectId
        const suffix = `${isLastElement ? ` (${workflow.triggerReferenceIds.length})` : ","}`

        if (triggerObjectId === ALL_TRIGGER_OBJECTS) return <div>{`${t("input:workflow.trigger.all")} (${totalNumForAll || 0})`}</div>
        if (workflow.triggerType === WorkflowTriggerEnum.USER) {
            const user = employees.find((employee) => employee.id === triggerObjectId)
            if (user?.activeCompanyProfile.deleted) {
                return (
                    <div className="mr-2">
                        {label}
                        <Tooltip
                            getPopupContainer={getTooltipPopupContainer}
                            autoAdjustOverflow={false}
                            className="ml-2"
                            placement="right"
                            title={t("info:employee_deleted")}
                            color={ORANGE}
                        >
                            <ExclamationCircleTwoTone twoToneColor={ORANGE} className="ml-5 inline-block transform -translate-y-2" />
                        </Tooltip>
                        {suffix}
                    </div>
                )
            }
        }

        return <div className="mr-2">{`${label}${suffix}`}</div>
    }

    /**
     * This makes sure that when we select ALL everything else is deselected. Vice versa,
     * it ensures that if ALL is selected and we select something else, then ALL will be deselected.
     */
    const onSelectTriggerObjects = (values: any[]) => {
        const wasAllOptionSelected = workflow.triggerReferenceIds.includes(ALL_TRIGGER_OBJECTS) && workflow.triggerReferenceIds.length === 1
        const didSelectAllOption = values.includes(ALL_TRIGGER_OBJECTS) && !wasAllOptionSelected

        if (didSelectAllOption) {
            updateWorkflow({ triggerReferenceIds: [ALL_TRIGGER_OBJECTS] })
        } else {
            updateWorkflow({ triggerReferenceIds: values.filter((v) => v !== ALL_TRIGGER_OBJECTS) })
        }
    }

    const onFinish = (values: Store) => {
        // if only the ALL option was selected, then unset the trigger IDs and set the generalized flag
        if (values.triggerReferenceIds?.includes(ALL_TRIGGER_OBJECTS) && values.triggerReferenceIds?.length === 1) {
            values.triggerReferenceIds = []
            values.isGeneralized = true
        } else {
            values.isGeneralized = false
        }

        // parse the currency input fields and approver fields
        if (values.triggerReferenceValue) values.triggerReferenceValue = parseCurrencyInput(values.triggerReferenceValue)
        values.rules = values.rules.map((rule: WorkflowRule) => ({
            ...rule,
            approverData: convertApproverFormData(rule.approverData),
            secondApprover: convertApproverFormData(rule.secondApprover),
            amount: rule.amount ? parseCurrencyInput(rule.amount) : undefined,
        }))

        onSubmit(values)
    }

    return (
        <Form layout="vertical" form={formInstance} onFinish={onFinish}>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Form.Item label={t("input:workflow.scope.scope")} name="scope">
                        <Radio.Group value={WorkflowScopeEnum.ALL} name="scope">
                            <Radio value={WorkflowScopeEnum.ALL}>{t("input:workflow.scope.all")}</Radio>
                            <Radio value={WorkflowScopeEnum.PURCHASE}>{t("input:workflow.scope.purchase")}</Radio>
                            <Radio value={WorkflowScopeEnum.INVOICE}>{t("input:workflow.scope.invoice")}</Radio>
                            <Radio value={WorkflowScopeEnum.REIMBURSEMENT}>{t("input:workflow.scope.reimbursement")}</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Form.Item label={t("input:workflow.name")} name="name" required rules={rules.name}>
                        <Input placeholder={t("placeholder:workflow.name")} />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col span={12}>
                    <Form.Item label={t("input:workflow.trigger.trigger_type")} name="triggerType">
                        <Select getPopupContainer={getPopupAnchor()} onChange={(v) => updateWorkflow({ triggerType: v as WorkflowTriggerEnum, triggerReferenceIds: [] })}>
                            {costCenters.length > 0 && <Select.Option value={WorkflowTriggerEnum.COST_CENTER}>{t("input:workflow.trigger.costcenter")}</Select.Option>}
                            {vendors.length > 0 && <Select.Option value={WorkflowTriggerEnum.VENDOR}>{t("input:workflow.trigger.vendor")}</Select.Option>}
                            {employees.length > 0 && <Select.Option value={WorkflowTriggerEnum.USER}>{t("input:workflow.trigger.user")}</Select.Option>}
                            {costCenter2s.length > 0 && <Select.Option value={WorkflowTriggerEnum.COST_CENTER_2}>{t("input:workflow.trigger.costcenter2")}</Select.Option>}
                            <Select.Option value={WorkflowTriggerEnum.AMOUNT}>{t("input:workflow.trigger.amount")}</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    {workflow.triggerType === WorkflowTriggerEnum.AMOUNT ? (
                        <Form.Item label={t(`input:workflow.trigger.amount_greater`)} name="triggerReferenceValue" required rules={rules.triggerReferenceValue}>
                            <CurrencyInput
                                min={0}
                                value={0}
                                currency={workflow.triggerReferenceCurrency || CurrencyEnum.EUR}
                                onChange={(_a, value) => updateWorkflow({ triggerReferenceValue: value })}
                            />
                        </Form.Item>
                    ) : (
                        <Form.Item
                            label={t(`input:workflow.trigger.${(workflow.triggerType as any)?.toLowerCase()}`)}
                            name="triggerReferenceIds"
                            required
                            rules={rules.triggerReferenceIds}
                        >
                            <Select
                                showArrow
                                mode="multiple"
                                getPopupContainer={getPopupAnchor()}
                                tagRender={({ value }) => renderTriggerObjectLabel(value)}
                                onChange={(values: any[]) => onSelectTriggerObjects(values)}
                                filterOption={(input, option) =>
                                    option?.value && (getTriggerObjectContext(option.value).label || "").toLocaleLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Select.Option value={ALL_TRIGGER_OBJECTS}>{t("input:workflow.trigger.all")}</Select.Option>
                                {resources.map((resource: any, index: number) => renderTriggerObjectOption(resource, workflow.triggerType, index, showCostCentersIds))}
                            </Select>
                        </Form.Item>
                    )}
                </Col>
            </Row>
            {workflow.triggerType === WorkflowTriggerEnum.AMOUNT && (
                <Col span={11} sm="24" md="24">
                    <Form.Item
                        name="triggerReferenceCurrency"
                        label={t("input:request.currency")}
                        key="currency"
                        initialValue={workflow.triggerReferenceCurrency || CurrencyEnum.EUR}
                    >
                        <Select showSearch getPopupContainer={getPopupAnchor()} onChange={(v) => updateWorkflow({ triggerReferenceCurrency: v })}>
                            {currencyEnumArray.map((currencyValue, index) => (
                                <Select.Option key={index} value={currencyValue} label={currencyValue}>
                                    <div className="space-x-10">
                                        <Image className="align-middle" width={20} preview={false} src={`data:image/png;base64,${Currencies[currencyValue].flag}`} />
                                        <span className="text-text">{currencyValue}</span>
                                        <span className="text-text">-</span>
                                        <span className="text-text">{Currencies[currencyValue].name}</span>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            )}
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <WorkflowFormRules workflow={workflow} updateWorkflow={updateWorkflow} possibleApprovers={possibleApprovers} rules={rules} formInstance={formInstance} />
                </Col>
            </Row>
        </Form>
    )
}

export default WorkflowForm
